import striptags from 'striptags';
import nunjucks from 'nunjucks';
import { content } from '@/api';
import {
  CE_MAIN_BRANCH,
  CE_DRAFT_BRANCH,
  TOC_VERSION_1,
  TOC_VERSION_2,
  CE_MODULE_TYPES,
  MODULE_UPDATE_TYPE,
  VIEW_TYPES
} from '@/constants';

import * as types from '@/store/mutationTypes';
import { mergeFieldToLowerCase, decodeHtmlEntities } from '@/lib/util';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';

let dataEngine = null;

const md = require('markdown-it')({
  html: true
});

md.use(markdownItFancyListPlugin);

const TOC_PATH_TYPES = {
  TYPE_MASTER_ONLY: 1,
  TYPE_DRAFT_INCLUDED: 2
};

const MANAGER_STATE = {
  STATE_IDLE: 0,
  STATE_LOADING: 1,
  STATE_READY: 2,
  STATE_UPDATING: 3,
  STATE_UPDATED: 4
};

const MANAGER_TYPE = {
  TYPE_TOC_MASTER: 0,
  TYPE_TOC_DRAFT: 1,
  TYPE_MODULE: 2
};

function ownedByUser(clientId, userID) {
  return clientId === userID;
}

function getTOCPathType(module, userID) {
  let res = TOC_PATH_TYPES.TYPE_MASTER_ONLY;
  if (module.moduletype === CE_MODULE_TYPES.TYPE_COMPLISPACE) {
    if (module.tocVersion === TOC_VERSION_2) {
      res = TOC_PATH_TYPES.TYPE_DRAFT_INCLUDED;
    }
  } else if (module.tocVersion === TOC_VERSION_1) {
    if (ownedByUser(module.clientId, userID)) {
      res = TOC_PATH_TYPES.TYPE_DRAFT_INCLUDED;
    } else {
      res = TOC_PATH_TYPES.TYPE_MASTER_ONLY;
    }
  } else {
    res = TOC_PATH_TYPES.TYPE_DRAFT_INCLUDED;
  }

  return res;
}

async function loadTOC(moduleID, branch) {
  return content.getTocs(moduleID, branch);
}

function compareTOC(tocA, tocB) {
  return tocA.moduleId === tocB.moduleId;
}

function compareGeneral(eleA, eleB) {
  return eleA.id === eleB.id;
}

class DATA_MANAGER {
  constructor() {
    this.userID = -1;
    this.modules = [];
    this.stateModule = MANAGER_STATE.STATE_IDLE;
    this.tocsMaster = [];
    this.tocsDraft = [];
    this.stateTOCMaster = MANAGER_STATE.STATE_IDLE;
    this.stateTOCDraft = MANAGER_STATE.STATE_IDLE;
  }

  reset(init) {
    this.userID = init.userID;
  }

  resetDataEngine(type) {
    switch (type) {
      case MANAGER_TYPE.TYPE_TOC_MASTER:
        this.stateTOCMaster = [];
        break;

      case MANAGER_TYPE.TYPE_TOC_DRAFT:
        this.stateTOCDraft = [];
        break;

      case MANAGER_TYPE.TYPE_MODULE:
        this.modules = [];
        break;

      default:
        break;
    }
  }

  updateDataEngine(type, value) {
    let engine = null;
    let com = null;
    switch (type) {
      case MANAGER_TYPE.TYPE_TOC_MASTER:
        engine = this.tocsMaster;
        com = compareTOC;
        break;

      case MANAGER_TYPE.TYPE_TOC_DRAFT:
        engine = this.tocsDraft;
        com = compareTOC;
        break;

      case MANAGER_TYPE.TYPE_MODULE:
        engine = this.modules;
        com = compareGeneral;
        break;
      default:
        break;
    }

    if (!(value in engine)) {
      let i = 0;
      let find = false;
      for (i = 0; i < engine.length; i += 1) {
        if (com(engine[i], value)) {
          engine[i] = value;
          find = true;
        }
      }
      if (!find) {
        engine.push(value);
      }
    }
  }

  updateModule(module) {
    this.updateDataEngine(MANAGER_TYPE.TYPE_MODULE, module);
  }

  updateTOC(toc, branch) {
    if (branch === CE_MAIN_BRANCH) {
      this.updateDataEngine(MANAGER_TYPE.TYPE_TOC_MASTER, toc);
    } else {
      this.updateDataEngine(MANAGER_TYPE.TYPE_TOC_DRAFT, toc);
    }
  }

  getTOC(id) {
    return this.m_tocs[id];
  }

  getModule(id) {
    return this.modules[id];
  }

  setEngineState(state, type) {
    switch (type) {
      case MANAGER_TYPE.TYPE_TOC_MASTER:
        this.stateTOCMaster = state;
        break;

      case MANAGER_TYPE.TYPE_TOC_DRAFT:
        this.stateTOCDraft = state;
        break;

      case MANAGER_TYPE.TYPE_MODULE:
        this.stateModule = state;
        break;

      default:
        break;
    }
  }

  getEngineState(type) {
    let ret = -1;
    switch (type) {
      case MANAGER_TYPE.TYPE_TOC_MASTER:
        ret = this.stateTOCMaster;
        break;

      case MANAGER_TYPE.TYPE_TOC_DRAFT:
        ret = this.stateTOCDraft;
        break;

      case MANAGER_TYPE.TYPE_MODULE:
        ret = this.stateModule;
        break;

      default:
        break;
    }
    return ret;
  }

  get moduleSum() {
    return this.modules.length;
  }
}

function addModules(commit, modules) {
  dataEngine.resetDataEngine(MANAGER_TYPE.TYPE_MODULE);

  modules.forEach((element) => {
    dataEngine.updateModule(element);
  });
  dataEngine.stateModule = MANAGER_STATE.STATE_READY;
  commit(types.SET_MODULE_STATE, MANAGER_STATE.STATE_READY);
}

function initial(commit, init) {
  if (!dataEngine) {
    dataEngine = new DATA_MANAGER();
  }

  dataEngine.reset(init);
  commit(types.INITIAL_DATA_MANAGER, true);
}

function setTOCReady(commit) {
  if (
    dataEngine.getEngineState(MANAGER_TYPE.TYPE_TOC_MASTER) === MANAGER_STATE.STATE_READY &&
    dataEngine.getEngineState(MANAGER_TYPE.TYPE_TOC_DRAFT) === MANAGER_STATE.STATE_READY
  ) {
    commit(types.SET_TOC_STATE, MANAGER_STATE.STATE_READY);
  }
}

function loadSingleTOCFromServer(commit, moduleId) {
  const module = dataEngine.modules.find((ele) => ele.id === moduleId);

  if (module) {
    let tocPromisesMaster = null;
    let tocPromisesDraft = null;

    const moduleType = getTOCPathType(module, dataEngine.userID);

    switch (moduleType) {
      case TOC_PATH_TYPES.TYPE_MASTER_ONLY:
        tocPromisesMaster = loadTOC(module.id, CE_MAIN_BRANCH);
        break;

      case TOC_PATH_TYPES.TYPE_DRAFT_INCLUDED:
        tocPromisesMaster = loadTOC(module.id, CE_MAIN_BRANCH);
        tocPromisesDraft = loadTOC(module.id, CE_DRAFT_BRANCH);
        break;

      default:
        break;
    }

    commit(types.SET_TOC_STATE, MANAGER_STATE.STATE_LOADING);
    dataEngine.setEngineState(MANAGER_STATE.STATE_LOADING, MANAGER_TYPE.TYPE_TOC_MASTER);

    if (tocPromisesDraft != null) {
      dataEngine.setEngineState(MANAGER_STATE.STATE_LOADING, MANAGER_TYPE.TYPE_TOC_DRAFT);
    }

    if (tocPromisesMaster != null) {
      Promise.all([tocPromisesMaster])
        .then((value) => {
          dataEngine.updateTOC(value[0].data[0], CE_MAIN_BRANCH);
          dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_MASTER);
          setTOCReady(commit);
        })
        .catch((error) => {
          console.log(error);
        });
    }

    if (tocPromisesDraft != null) {
      Promise.all([tocPromisesDraft])
        .then((value) => {
          dataEngine.updateTOC(value[0].data[0], CE_DRAFT_BRANCH);
          dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_DRAFT);
          setTOCReady(commit);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }
}

function loadTOCFromServer(commit) {
  const tocMaster = [];
  const tocDraft = [];
  let moduleType = 0;

  if (dataEngine.stateModule === MANAGER_STATE.STATE_READY) {
    dataEngine.modules.forEach((m) => {
      moduleType = getTOCPathType(m, dataEngine.userID);
      switch (moduleType) {
        case TOC_PATH_TYPES.TYPE_MASTER_ONLY:
          tocMaster.push(m.id);
          break;

        case TOC_PATH_TYPES.TYPE_DRAFT_INCLUDED:
          tocMaster.push(m.id);
          tocDraft.push(m.id);
          break;

        default:
          break;
      }
    });

    if (tocMaster.length > 0) {
      dataEngine.setEngineState(MANAGER_STATE.STATE_LOADING, MANAGER_TYPE.TYPE_TOC_MASTER);
      const tocPromisesMaster = loadTOC(tocMaster, CE_MAIN_BRANCH);
      Promise.all([tocPromisesMaster])
        .then((values) => {
          if (values[0].data) {
            values[0].data.forEach((value) => {
              dataEngine.updateTOC(value, CE_MAIN_BRANCH);
            });
          }

          dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_MASTER);
          setTOCReady(commit);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_MASTER);
    }

    if (tocDraft.length > 0) {
      dataEngine.setEngineState(MANAGER_STATE.STATE_LOADING, MANAGER_TYPE.TYPE_TOC_DRAFT);
      const tocPromisesDraft = loadTOC(tocDraft, CE_DRAFT_BRANCH);

      Promise.all([tocPromisesDraft])
        .then((values) => {
          if (values[0].data) {
            values[0].data.forEach((value) => {
              dataEngine.updateTOC(value, CE_DRAFT_BRANCH);
            });
          }
          dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_DRAFT);
          setTOCReady(commit);
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_DRAFT);
    }
  }
}

async function loadTOCFromServerAsync(commit) {
  const tocMaster = [];
  const tocDraft = [];
  let moduleType = 0;

  if (dataEngine.stateModule === MANAGER_STATE.STATE_READY) {
    dataEngine.modules.forEach((m) => {
      moduleType = getTOCPathType(m, dataEngine.userID);
      switch (moduleType) {
        case TOC_PATH_TYPES.TYPE_MASTER_ONLY:
          tocMaster.push(m.id);
          break;

        case TOC_PATH_TYPES.TYPE_DRAFT_INCLUDED:
          tocMaster.push(m.id);
          tocDraft.push(m.id);
          break;

        default:
          break;
      }
    });

    if (tocMaster.length > 0) {
      dataEngine.setEngineState(MANAGER_STATE.STATE_LOADING, MANAGER_TYPE.TYPE_TOC_MASTER);
      const tocMasterResults = await loadTOC(tocMaster, CE_MAIN_BRANCH);
      if (tocMasterResults.data) {
        tocMasterResults.data.forEach((value) => {
          dataEngine.updateTOC(value, CE_MAIN_BRANCH);
        });
      }

      dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_MASTER);
      setTOCReady(commit);
    } else {
      dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_MASTER);
    }

    if (tocDraft.length > 0) {
      dataEngine.setEngineState(MANAGER_STATE.STATE_LOADING, MANAGER_TYPE.TYPE_TOC_DRAFT);
      const tocDraftResults = loadTOC(tocDraft, CE_DRAFT_BRANCH);

      if (tocDraftResults.data) {
        tocDraftResults.data.forEach((value) => {
          dataEngine.updateTOC(value, CE_DRAFT_BRANCH);
        });
      }
      dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_DRAFT);
      setTOCReady(commit);
    } else {
      dataEngine.setEngineState(MANAGER_STATE.STATE_READY, MANAGER_TYPE.TYPE_TOC_DRAFT);
    }
  }
}

function getAllModules(branch) {
  let res = null;
  if (
    dataEngine &&
    dataEngine.getEngineState(MANAGER_TYPE.TYPE_MODULE) === MANAGER_STATE.STATE_READY
  ) {
    if (branch === CE_MAIN_BRANCH && dataEngine.moduleSum > 0) {
      res = dataEngine.modules.filter((module) => module.hasPublishedContents);
    } else {
      res = dataEngine.modules;
    }
  }
  return res;
}

/* eslint-disable no-param-reassign */

function getAllLeafChildren(tocPage, list) {
  if (tocPage.children && tocPage.children.length > 0) {
    tocPage.children.forEach((child) => {
      if (child.children && child.children.length > 0) {
        getAllLeafChildren(child, list);
      } else {
        list.push(child);
      }
    });
  }
}

function getParentToc(page, toc) {
  let res = null;

  if (toc && toc.path !== page) {
    if (toc.children && toc.children.length > 0) {
      if (toc.children.findIndex((ele) => ele.path === page) !== -1) {
        res = toc;
      } else {
        toc.children.some((child) => {
          res = getParentToc(page, child);
          if (res) {
            return true;
          }
          return false;
        });
      }
    }
  }
  return res;
}

function setTocProperty(toc, viewType, treeShape) {
  const clientId = dataEngine.userID;
  const childrenList = [];
  getAllLeafChildren(toc, childrenList);

  function setSelected(toSet, selected) {
    if (toSet) {
      toc.selected = selected;
    }
  }

  function setVisible(toSetSelected) {
    if (toc.visible) {
      // it probably needs to change visible to false if parent is not visible
      // it only happends when child client add some pages under parent page, then the page is closed by its ancestor
      const parent = getParentToc(toc.path, treeShape);

      if (parent && !parent.visible) {
        toc.visible = false;
        setSelected(toSetSelected, false);
      } else {
        setSelected(toSetSelected, true);
      }
    } else {
      // toc visible probably needs to change to true if the not visible is set by itself.
      // eslint-disable-next-line no-lonely-if
      if (toc.ownerId) {
        if (toc.ownerId === clientId) {
          // closed by itself
          const parent = getParentToc(toc.path, treeShape);
          if (parent && !parent.visible) {
            toc.visible = false;
          } else {
            toc.visible = true;
          }
        }
        // else closed by its ancestor
        setSelected(toSetSelected, false);
      }
    }
  }

  function setClientVisible() {
    if (toc.visible) {
      const parent = getParentToc(toc.path, treeShape);
      if (parent) {
        if (!parent.visible) {
          // it is the only logic needs to amend here
          toc.visible = false;
        }
      }
    }
  }

  switch (viewType) {
    case VIEW_TYPES.ADMIN_PUBLISH:
      setVisible(true);
      break;

    case VIEW_TYPES.ADMIN_DRAFT:
      setVisible(true);
      break;

    case VIEW_TYPES.CLIENT:
    default:
      setClientVisible();
      break;
  }
}

function cleanChildren(toc, context, viewType, treeShape) {
  toc.vpath = toc.path;
  setTocProperty(toc, viewType, treeShape);

  if (toc.children && toc.children.length === 0) {
    delete toc.children;
  } else if (toc.children) {
    const ptoc = JSON.parse(JSON.stringify(toc));
    ptoc.parentId = toc.id;
    ptoc.children = [];
    toc.children.forEach((child) => {
      cleanChildren(child, context, viewType, treeShape);
    });
  }
  try {
    let text = nunjucks.renderString(md.render(mergeFieldToLowerCase(toc.name)), context);
    text = decodeHtmlEntities(text);
    toc.name = unescape(striptags(text));
  } catch (error) {
    console.error(`Error while rendering page title ${toc.name} with context key values: `, error);
  }

  return toc;
}

function getTOCFromID(branch, moduleId, context, viewType) {
  let res = null;
  if (!dataEngine) {
    return res;
  }

  if (branch === CE_MAIN_BRANCH) {
    res = dataEngine.tocsMaster.find((ele) => ele.moduleId === moduleId);
  } else {
    res = dataEngine.tocsDraft.find((ele) => ele.moduleId === moduleId);
    if (!res) {
      res = dataEngine.tocsMaster.find((ele) => ele.moduleId === moduleId);
    }
  }

  if (res && res.toc) {
    const ptoc = JSON.parse(JSON.stringify(res.toc));

    res = cleanChildren(ptoc, context, viewType, ptoc);
  }
  return res;
}

function updateSingleModule(id, type, value) {
  const index = dataEngine.modules.findIndex((ele) => ele.id === id);

  if (index !== -1) {
    switch (type) {
      case MODULE_UPDATE_TYPE.TYPE_HAS_UNPUBLISH_CONTENT:
        dataEngine.modules.splice(index, 1, {
          ...dataEngine.modules[index],
          hasUnpublishedDraftContent: value
        });
        break;

      default:
        break;
    }
  }
}

function addSingleModule(commit, module) {
  dataEngine.updateModule(module);
}

export default {
  addModules,
  addSingleModule,
  initial,
  loadTOCFromServer,
  loadTOCFromServerAsync,
  loadSingleTOCFromServer,
  getAllModules,
  getTOCFromID,
  updateSingleModule,
  MANAGER_STATE,
  MANAGER_TYPE
};
